import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import { FaSpinner, FaChevronLeft, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import NewsletterSignup from '../components/NewsletterSignup';
import './Blog.css';

const Blog = () => {
  const navigate = useNavigate();
  
  // Blog data
  const [blogs, setBlogs] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const blogsPerPage = 6;

  useEffect(() => {
    fetchBlogs();
  }, [currentPage]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * blogsPerPage;
      const params = {
        limit: blogsPerPage,
        skip,
        status: 'published'
      };
      
      const response = await axios.get(`${API_URL}/blogs`, { params });
      
      if (response.data.success) {
        let allBlogs = response.data.data;
        
        // If it's the first page, set the first blog as featured
        if (currentPage === 1 && allBlogs.length > 0) {
          setFeaturedBlog(allBlogs[0]);
          setBlogs(allBlogs.slice(1));
        } else {
          setBlogs(allBlogs);
        }
        
        setTotalPages(response.data.pagination.pages);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleBlogClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => {
            setCurrentPage(i);
            window.scrollTo(0, 0);
          }}
          className={`pagination-number ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading && currentPage === 1) {
    return (
      <div className="flex justify-center items-center h-screen bg-white">
        <FaSpinner className="animate-spin text-blue-600 text-5xl" />
      </div>
    );
  }

  return (
    <div className="modern-blog-container bg-white pt-12 md:pt-12">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Featured Blog Section */}
        {featuredBlog && currentPage === 1 && (
          <article 
            className="featured-post-wrapper mb-10 md:mb-20 cursor-pointer"
            onClick={() => handleBlogClick(featuredBlog.slug)}
          >
            <div className="featured-image-container rounded-2xl overflow-hidden mb-4 md:mb-8 shadow-sm group">
              <img 
                src={featuredBlog.featuredImage} 
                alt={featuredBlog.title} 
                className="w-full h-[250px] md:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700" 
              />
            </div>
            <div className="featured-content-info text-left">
              <h1 className="featured-main-title text-4xl md:text-6xl font-black text-gray-900 mb-4 md:mb-6 uppercase tracking-tight leading-[1.1]">
                {featuredBlog.title}
              </h1>
              <div className="featured-meta-info text-blue-600 font-bold mb-4 md:mb-6 text-sm">
                by {featuredBlog.authorName} | Published on {formatDate(featuredBlog.publishDate)}
              </div>
              <p className="featured-excerpt text-gray-500 text-lg leading-relaxed max-w-4xl font-medium mb-6 md:mb-10">
                {featuredBlog.description}
              </p>
            </div>
          </article>
        )}

        {/* Secondary Featured Section (New) */}
        {blogs.length >= 2 && currentPage === 1 && (
          <section className="secondary-featured-section mb-12 md:mb-24">
            <h2 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6 pb-2 md:mb-10 md:pb-4 border-b border-gray-100">Trending Stories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
              {blogs.slice(0, 2).map((blog) => (
                <article 
                  key={blog._id} 
                  className="secondary-featured-post cursor-pointer group"
                  onClick={() => handleBlogClick(blog.slug)}
                >
                  <div className="rounded-2xl overflow-hidden mb-4 md:mb-6 aspect-[16/10] shadow-sm">
                    <img 
                      src={blog.featuredImage} 
                      alt={blog.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                    />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 uppercase tracking-tight leading-tight">
                    {blog.title}
                  </h3>
                  <div className="text-blue-600 font-bold text-xs uppercase tracking-widest mb-4">
                    {blog.category} • {blog.readingTime}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <hr className="border-gray-100 mb-8 md:mb-16" />

        {/* Similar Blogs Grid */}
        <section className="similar-blogs-section mb-10 md:mb-20">
          <h2 className="section-heading text-4xl font-black text-gray-900 mb-6 md:mb-12 flex items-center gap-2">
            More Stories
          </h2>

          {(currentPage === 1 ? blogs.slice(2) : blogs).length === 0 ? (
            <div className="text-center py-20 text-gray-400 font-medium">No more blogs found.</div>
          ) : (
            <div className="blogs-modern-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 md:gap-y-16">
              {(currentPage === 1 ? blogs.slice(2) : blogs).map((blog) => (
                <div 
                  key={blog._id} 
                  className="modern-blog-card group cursor-pointer"
                  onClick={() => handleBlogClick(blog.slug)}
                >
                  <div className="card-image-wrap rounded-xl overflow-hidden mb-4 md:mb-6 aspect-video">
                    <img 
                      src={blog.featuredImage} 
                      alt={blog.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                    />
                  </div>
                  <div className="card-body">
                    <span className="read-time-badge text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
                      {blog.readingTime}
                    </span>
                    <h3 className="card-title text-2xl font-bold text-gray-900 mb-4 leading-tight font-serif min-h-[64px] line-clamp-2">
                      {blog.title}
                    </h3>
                    <p className="card-desc text-gray-500 text-sm font-medium line-clamp-2 mb-4 md:mb-6 uppercase tracking-wider">
                      {blog.description}
                    </p>
                    <button className="modern-read-more flex items-center gap-2 text-xs font-black text-gray-900 border-2 border-gray-100 rounded-full px-5 py-2.5 group-hover:bg-gray-900 group-hover:text-white group-hover:border-gray-900 transition-all">
                      Read more <FaArrowRight className="text-[10px]" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="modern-pagination flex items-center justify-center gap-3 mb-12 md:mb-24">
            <button 
              onClick={() => {
                setCurrentPage(p => Math.max(1, p - 1));
                window.scrollTo(0, 0);
              }}
              disabled={currentPage === 1}
              className="pagination-arrow disabled:opacity-30 p-2 hover:bg-gray-100 rounded-full transition-all"
            >
              <FaChevronLeft className="text-gray-400" />
            </button>
            <div className="flex items-center gap-2">
              {renderPagination()}
            </div>
            <button 
              onClick={() => {
                setCurrentPage(p => Math.min(totalPages, p + 1));
                window.scrollTo(0, 0);
              }}
              disabled={currentPage === totalPages}
              className="pagination-arrow disabled:opacity-30 p-2 hover:bg-gray-100 rounded-full transition-all"
            >
              <FaChevronRight className="text-gray-400" />
            </button>
          </div>
        )}

        {/* Brand Newsletter Section */}
        <section className="bg-gray-50 rounded-2xl md:rounded-[40px] p-6 md:p-16 mb-12 md:mb-24 flex flex-col md:flex-row items-center justify-between gap-10 overflow-hidden relative">
          <div className="relative z-10">
            <h2 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">Stay updated!</h2>
            <p className="text-gray-500 font-bold max-w-md">Subscribe to our newsletter for the latest travel guides and offers.</p>
          </div>
          <div className="w-full md:w-auto relative z-10">
             <NewsletterSignup showHeader={false} darkMode={true} />
          </div>
        </section>

      </div>
    </div>
  );
};

export default Blog;
